import React, { useEffect, useState } from "react";

const Key = ({ label, onClick, variant = "num", testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`h-11 rounded-md text-sm font-semibold transition-colors ${
      variant === "num" ? "bg-white hover:bg-slate-100 border border-slate-200"
      : variant === "op" ? "bg-slate-100 hover:bg-slate-200 border border-slate-200"
      : "bg-sky-600 hover:bg-sky-500 text-white"
    }`}
  >
    {label}
  </button>
);

/** Windows-11-style standard calculator (client-side only). */
export default function CalculatorApp() {
  const [display, setDisplay] = useState("0");
  const [acc, setAcc] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);

  const input = (d) => {
    setDisplay((cur) => {
      if (fresh || cur === "0") { setFresh(false); return d === "." ? "0." : d; }
      if (d === "." && cur.includes(".")) return cur;
      return cur.length < 14 ? cur + d : cur;
    });
  };

  const apply = (a, b, o) => {
    const r = o === "+" ? a + b : o === "−" ? a - b : o === "×" ? a * b : b === 0 ? NaN : a / b;
    return Number.isFinite(r) ? Math.round(r * 1e10) / 1e10 : NaN;
  };

  const pressOp = (o) => {
    const cur = parseFloat(display);
    if (acc !== null && op && !fresh) {
      const r = apply(acc, cur, op);
      setAcc(r); setDisplay(Number.isNaN(r) ? "Error" : String(r));
    } else {
      setAcc(cur);
    }
    setOp(o); setFresh(true);
  };

  const equals = () => {
    if (acc === null || !op) return;
    const r = apply(acc, parseFloat(display), op);
    setDisplay(Number.isNaN(r) ? "Error" : String(r));
    setAcc(null); setOp(null); setFresh(true);
  };

  const clear = () => { setDisplay("0"); setAcc(null); setOp(null); setFresh(true); };
  const back = () => setDisplay((c) => (fresh || c.length <= 1 ? "0" : c.slice(0, -1)));
  const pct = () => setDisplay((c) => String(parseFloat(c) / 100));
  const neg = () => setDisplay((c) => (c.startsWith("-") ? c.slice(1) : c === "0" ? c : "-" + c));

  // Full keyboard support: digits, . + - * / %, Enter/=, Backspace, Esc/C
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const k = e.key;
      if (/^[0-9]$/.test(k)) { e.preventDefault(); input(k); }
      else if (k === ".") { e.preventDefault(); input("."); }
      else if (k === "+") { e.preventDefault(); pressOp("+"); }
      else if (k === "-") { e.preventDefault(); pressOp("−"); }
      else if (k === "*") { e.preventDefault(); pressOp("×"); }
      else if (k === "/") { e.preventDefault(); pressOp("÷"); }
      else if (k === "Enter" || k === "=") { e.preventDefault(); equals(); }
      else if (k === "Backspace") { e.preventDefault(); back(); }
      else if (k === "Escape" || k.toLowerCase() === "c") { e.preventDefault(); clear(); }
      else if (k === "%") { e.preventDefault(); pct(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="h-full flex flex-col p-3 bg-slate-50" data-testid="calc-app">
      <div className="text-right text-xs text-slate-400 h-4">{acc !== null && op ? `${acc} ${op}` : ""}</div>
      <div className="text-right text-4xl font-bold mb-3 truncate" data-testid="calc-display">{display}</div>
      <div className="grid grid-cols-4 gap-1.5 flex-1">
        <Key label="%" variant="op" onClick={pct} />
        <Key label="C" variant="op" onClick={clear} testid="calc-clear" />
        <Key label="⌫" variant="op" onClick={back} />
        <Key label="÷" variant="op" onClick={() => pressOp("÷")} />
        {["7", "8", "9"].map((d) => <Key key={d} label={d} onClick={() => input(d)} testid={`calc-${d}`} />)}
        <Key label="×" variant="op" onClick={() => pressOp("×")} />
        {["4", "5", "6"].map((d) => <Key key={d} label={d} onClick={() => input(d)} testid={`calc-${d}`} />)}
        <Key label="−" variant="op" onClick={() => pressOp("−")} />
        {["1", "2", "3"].map((d) => <Key key={d} label={d} onClick={() => input(d)} testid={`calc-${d}`} />)}
        <Key label="+" variant="op" onClick={() => pressOp("+")} testid="calc-plus" />
        <Key label="±" onClick={neg} />
        <Key label="0" onClick={() => input("0")} testid="calc-0" />
        <Key label="." onClick={() => input(".")} />
        <Key label="=" variant="eq" onClick={equals} testid="calc-equals" />
      </div>
    </div>
  );
}
